import type { LocationQuery } from "vue-router";

/** The sign-in page's query parameter saying why the member was sent there. */
export const SignInReasonQueryKey = 'reason' as const;

/** Why the member landed on sign-in; each reason has one line of copy above the form. */
export type SignInReason = 'password-reset' | 'password-changed' | 'expired' | 'signed-out-elsewhere';

/** The reasons that say the session ended under the member; the form shows them as information, not a success. */
export const SessionEndedSignInReasons: readonly SignInReason[] = [ 'expired', 'signed-out-elsewhere' ];

export const SignInNoticeCopy: Readonly< Record< SignInReason, string > > = {
    // UX roadmap #43: a finished reset used to end on a page with nowhere to go. Neutral copy: the same flow sets a new
    // paid subscriber's first password.
    'password-reset': 'Your password is set. Sign in with your new password.',
    // UX roadmap #87: a change from Profile used to sign the member out onto the public home page.
    'password-changed': 'Your password was changed. Sign in with your new password.',
    // UX roadmap #16: the session ended under the member (Application.handleUnauthorizedSessionExpiry). They used to land
    // on a bare form with no idea why they had been signed out.
    'expired': 'Your session expired, so we signed you out. Sign in again to pick up where you left off.',
    // UX roadmap #63: a sign-out in another tab used to leave this one showing the member's private pages.
    'signed-out-elsewhere': 'You signed out in another tab. Sign in again to pick up where you left off.',
};

/** The notice for a sign-in route's query, or null for no reason or one this build does not know. */
export function signInNoticeFor( query: LocationQuery | undefined ): string | null {
    const reason = query?.[ SignInReasonQueryKey ];
    return typeof reason === 'string' && Object.hasOwn( SignInNoticeCopy, reason )
        ? SignInNoticeCopy[ reason as SignInReason ]
        : null;
}
